// Turns a pasted or uploaded query result into rows and columns, so the app can show it as a table.
// Understands CSV, TSV, JSON (an array of objects), psql's aligned output and Markdown tables.
// Returns null when the text doesn't look like a table; the app then shows it as plain text.
// Exports often start with a few lines about the report (title, period, "Province,Kandal") before the real header:
// those come back as meta, one list of cells per line, and the table starts at the header.

export type Table = { columns: string[]; rows: (string | null)[][]; meta?: string[][] };

const SEP_LINE = /^\s*\|?[\s\-:+|]*-[\s\-:+|]*\|?\s*$/; // ----+---- or |---|:--:|

export function parseTable(text: string, name = ""): Table | null {
  text = text.replace(/^﻿/, "");
  const ext = name.toLowerCase().split(".").pop() ?? "";
  if (ext === "json" || /^\s*[[{]/.test(text)) {
    const t = fromJson(text);
    if (t || ext === "json") return t;
  }
  const lines = text.split(/\r?\n/);
  while (lines.length && !lines[0]!.trim()) lines.shift();
  if (lines.length >= 2 && lines[0]!.includes("|") && SEP_LINE.test(lines[1]!)) return fromPipes(lines);

  const delim = ext === "tsv" ? "\t" : ext === "csv" ? "," : guessDelimiter(lines);
  if (!delim) return null;
  const rows = parseDelimited(text, delim);
  const h = headerRow(rows);
  if (!rows.length || (rows[h]!.length < 2 && ext !== "csv" && ext !== "tsv")) return null;
  const t = pad(rows[h]!, rows.slice(h + 1));
  if (h > 0) t.meta = rows.slice(0, h).map((r) => r.map((c) => c.trim()).filter(Boolean)).filter((r) => r.length);
  return t;
}

/** The separator most lines use (a title line on top may have none): tab, then comma, then semicolon. */
function guessDelimiter(lines: string[]): string {
  const sample = lines.filter((l) => l.trim()).slice(0, 30);
  let best = "", most = 0;
  for (const d of ["\t", ",", ";"]) {
    const n = sample.filter((l) => l.includes(d)).length;
    if (n > most) { best = d; most = n; }
  }
  // one line is enough when it's the only line, or the first one (the old rule); otherwise most of a short paste
  return most >= 2 || (most === 1 && sample[0]?.includes(best)) ? best : "";
}

const width = (r: string[]) => { let n = r.length; while (n > 0 && !r[n - 1]!.trim()) n--; return n; };
/**
 * Where the table starts: the first row as wide as the table, when everything above it is one or two cells
 * (a title, "key,value" lines) and most rows below it are that wide too. Otherwise the first row.
 */
function headerRow(rows: string[][]): number {
  if (rows.length < 3) return 0;
  const widths = rows.map(width);
  const max = Math.max(...widths.slice(0, 200));
  if (max < 3) return 0;
  const h = widths.findIndex((w) => w === max);
  if (h <= 0 || h > 30 || widths.slice(0, h).some((w) => w > 2)) return 0;
  const below = widths.slice(h + 1);
  return below.length && below.filter((w) => w === max).length >= below.length / 2 ? h : 0;
}

function fromJson(text: string): Table | null {
  let v: unknown;
  try { v = JSON.parse(text); } catch { return null; }
  if (v && !Array.isArray(v) && typeof v === "object") v = Object.values(v).find(Array.isArray) ?? [v];
  if (!Array.isArray(v) || !v.length || !v.every((r) => r && typeof r === "object" && !Array.isArray(r))) return null;
  const columns = [...new Set(v.flatMap((r) => Object.keys(r)))];
  const cell = (x: unknown) => (x === null || x === undefined ? null : typeof x === "object" ? JSON.stringify(x) : String(x));
  return { columns, rows: v.map((r) => columns.map((c) => cell((r as Record<string, unknown>)[c]))) };
}

function fromPipes(lines: string[]): Table {
  const split = (l: string) => {
    const t = l.trim().replace(/^\|/, "").replace(/\|$/, "");
    return t.split("|").map((c) => c.trim());
  };
  const body = lines.slice(2).filter((l) => l.trim() && !/^\(\d+ rows?\)$/.test(l.trim()) && !SEP_LINE.test(l));
  return pad(split(lines[0]!), body.map(split));
}

/** RFC 4180-ish: quoted fields may hold the delimiter, newlines and "" for a quote. */
export function parseDelimited(text: string, d: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [], f = "", q = false, quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (q) {
      if (c !== '"') f += c;
      else if (text[i + 1] === '"') { f += '"'; i++; }
      else q = false;
    } else if (c === '"' && f === "" && !quoted) { q = quoted = true; }
    else if (c === d) { row.push(f); f = ""; quoted = false; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      row.push(f); rows.push(row);
      row = []; f = ""; quoted = false;
    } else f += c;
  }
  if (f !== "" || row.length) { row.push(f); rows.push(row); }
  return rows.filter((r) => !(r.length === 1 && r[0] === ""));
}

function pad(columns: string[], rows: string[][]): Table {
  const n = rows.reduce((m, r) => Math.max(m, r.length), columns.length);
  const cols = Array.from({ length: n }, (_, i) => columns[i] || `column ${i + 1}`);
  return { columns: cols, rows: rows.map((r) => Array.from({ length: n }, (_, i) => r[i] ?? "")) };
}
